import React from "react";
import { PillGroup, Pill } from "./ui/Pill";
import { Num } from "./ui/Num";
import { RecipientsGrid } from "./RecipientsGrid";
import { ENGAGEMENT_FILTERS } from "./broadcastEnums";

/**
 * The Engagement tab: who clicked, who only opened, who did neither.
 *
 * ---------------------------------------------------------------------------
 * WHY THE CAPTION IS NOT DECORATION
 * ---------------------------------------------------------------------------
 * An open is a tracking pixel being fetched. Mail privacy features — Apple Mail
 * Privacy Protection chief among them — prefetch that pixel on the recipient's
 * behalf, with no human in the loop, so an "open" can mean nothing happened at
 * all. A click is a real request from a real tap.
 *
 * That is why Clicked is the emphasised column everywhere else in this feature
 * (see `BroadcastRowGrid`, where only the Clicked counter gets `emphasis`), and
 * why the caption says so out loud here rather than leaving an author to
 * discover it from a suspiciously good open rate. Two numbers side by side with
 * no note read as equally trustworthy; they are not.
 *
 * PRESENTATIONAL ONLY. Nothing here fetches — the detail page owns the request
 * and hands the results down. Debouncing the search box is the PAGE's job, not
 * this component's: every keystroke calls `onQueryChange` straight through.
 */

const INPUT_CLASS =
  "w-full rounded-lg border border-bc-border bg-white px-3 py-1.5 text-[13px] text-bc-text placeholder:text-bc-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary";

/** Written whole so Tailwind keeps the classes; assembled names never ship. */
const CAPTION_CLASS = "text-xs text-bc-soft";

/**
 * The Engagement tab.
 *
 * @param {Object} props
 * @param {Array<Object>} props.recipients - Decoded recipient rows, one per person.
 * @param {boolean} [props.loading=false] - A fetch is in flight; the grid paints skeletons.
 * @param {string|null} [props.error] - A user-facing error message, or null.
 * @param {string} [props.filter] - Selected engagement id, one of ENGAGEMENT_FILTERS.
 * @param {(engagement: string) => void} [props.onFilterChange] - Called with the newly selected engagement id.
 * @param {string} [props.query] - The current search text.
 * @param {(query: string) => void} [props.onQueryChange] - Called with the raw input value on every keystroke.
 * @param {number|null} [props.total] - Server's total for the CURRENT filter. `null` renders an em dash.
 * @returns {React.ReactElement}
 */
export const EngagementTab = ({
  recipients,
  loading = false,
  error = null,
  filter,
  onFilterChange,
  query = "",
  onQueryChange,
  total = null,
}) => (
  <section className="flex flex-col gap-3">
    <div className="flex flex-wrap items-center gap-3">
      <PillGroup label="Engagement">
        {ENGAGEMENT_FILTERS.map((option) => (
          <Pill
            key={option.id}
            selected={option.id === filter}
            onSelect={() => onFilterChange?.(option.id)}
          >
            {option.label}
          </Pill>
        ))}
      </PillGroup>

      <div className="ml-auto w-full max-w-[240px]">
        <input
          type="search"
          aria-label="Search recipients"
          value={query}
          onChange={(event) => onQueryChange?.(event.target.value)}
          placeholder="Name, email or phone"
          className={INPUT_CLASS}
        />
      </div>
    </div>

    <p className={CAPTION_CLASS}>
      Opens are a soft signal — mail privacy features fetch tracking pixels with
      no human involved. Clicks are exact.
    </p>

    {/* `Num` so an unknown total is an em dash rather than a confident `0`. */}
    <p className={CAPTION_CLASS}>
      <Num value={total} /> recipients
    </p>

    {error ? (
      <p role="alert" className="text-xs text-bc-bad">
        {error}
      </p>
    ) : null}

    <RecipientsGrid
      recipients={recipients}
      loading={loading}
      lastColumn="engagement"
      emptyMessage="No recipients match this engagement."
    />
  </section>
);

export default EngagementTab;
